import find from "lodash/find";
import assign from "lodash/assign";
import alt from "../alt";
import OrderActions from "../actions/OrderActions";
import OrderUtil from "../utils/OrderUtil";
import TicketActions from "../actions/OrderActions";
import EventStore from "./EventStore";
import PersonStore from "./PersonStore";
import EventActions from "../actions/EventActions";
import PassActions from "../actions/PassActions";
import CreditActions from "../actions/CreditActions";
import AppStore from "../stores/AppStore";

import GlobalNotificationActions from "../actions/GlobalNotificationActions";
import { history } from "../util";

const DONATE_INTERSTITIAL_STORAGE_KEY = "eventive-donateInterstitialShown";

const SHOULD_PERSIST = "localStorage" in window;
const LS_KEY = "eventive-cartState";
let cachedCart;
try {
  cachedCart =
    (SHOULD_PERSIST &&
      window.localStorage[LS_KEY] &&
      JSON.parse(window.localStorage[LS_KEY])) ||
    {};
} catch (e) {
  // Catch if unable to access localstorage (e.g. due to DOM Exception 18)
  cachedCart = null;
}

class OrderStore {
  constructor() {
    this.bindActions(OrderActions);

    this.state = {
      // Completed orders
      orders: {},

      // For new orders
      orderData: {
        subitems: []
      },
      completedOrderId: null,
      orderSuccess: false,
      orderInProgress: false,
      orderErrorMessage: null,
      orderSeatTimeout: null,
      orderShowDonateInterstitial: false,

      // For cart
      cartData: Object.assign(
        {
          subitems: []
        },
        cachedCart
      )
    };

    this.exportPublicMethods({
      getCartQuantity: this.getCartQuantity.bind(this),
      getCartOrder: this.getCartOrder.bind(this),
      getOrderById: this.getOrderById.bind(this)
    });
  }

  getCartQuantity() {
    return (
      (this.state.cartData &&
        this.state.cartData.subitems &&
        this.state.cartData.subitems.reduce((t, i) => t + i.quantity, 0)) ||
      0
    );
  }

  getCartOrder() {
    return this.state.cartData || null;
  }

  getOrderById(id) {
    return this.state.orders[id];
  }

  onFetchAll() {
    OrderUtil.fetchAll(PersonStore.getState().token)
      .then(data => {
        OrderActions.updateOrders(indexBy(data, "id"));
      })
      .catch(OrderActions.fetchFailed);
  }

  onFetchOne(id) {
    OrderUtil.fetchOne(PersonStore.getState().token, id)
      .then(data => {
        OrderActions.updateOrder(data.id, data);
      })
      .catch(OrderActions.fetchFailed);
  }

  onUpdateOrders(events) {
    this.setState({ orders });
  }

  onUpdateOrder(data) {
    this.state.orders[data.id] = data.order;
    this.setState(this.state);
  }

  onNewOrderFlow(type) {
    this.setState({
      orderData: {
        subitems: []
      },
      completedOrderId: null,
      orderSuccess: false,
      orderInProgress: false,
      orderErrorMessage: null,
      orderSeatTimeout: null,
      orderShowDonateInterstitial: false
    });
  }

  onNewCartOrderFlow() {
    this.setState({
      orderData: this.state.cartData,
      completedOrderId: null,
      orderSuccess: false,
      orderInProgress: false,
      orderErrorMessage: null,
      orderSeatTimeout: null,
      orderShowDonateInterstitial: false
    });
  }

  onResetCart() {
    this.setState({
      cartData: {
        subitems: []
      }
    });
    OrderActions.newOrderFlow.defer();
  }

  // this is for the order payment wizard since it doesn't submit the order itself
  onStartOrderSubmission() {
    this.setState({
      orderInProgress: true
    });
  }

  onSubmitOrder(returnTo) {
    const guestCheckoutActiveEmail =
      PersonStore.getState().guestCheckoutActiveEmail;

    if (guestCheckoutActiveEmail) {
      this.state.orderData.guest_email = guestCheckoutActiveEmail;
      this.state.orderData.guest_mode = true;
    }

    // If enabled / forced, show the donate interstitial screen prior to placing the final order.
    let hasDonationAlready = this.state.orderData.subitems.some(
      i => i.inline_donation && i.price
    );
    let forceShowInterstitial = window.location.search.indexOf("_d=1") !== -1;
    if (
      !this.state.orderShowDonateInterstitial &&
      !hasDonationAlready &&
      (forceShowInterstitial ||
        ((this.state.orderData.event || this.state.orderData.pass_code) &&
          !this.state.orderData.pass &&
          TENANT.donate_one_time_interstitial &&
          !window.localStorage.getItem(DONATE_INTERSTITIAL_STORAGE_KEY)))
    ) {
      if (!forceShowInterstitial) {
        window.localStorage.setItem(DONATE_INTERSTITIAL_STORAGE_KEY, 1);
      }

      this.setState({
        orderShowDonateInterstitial: true
      });

      return;
    }

    this.setState({
      orderInProgress: true
    });

    if (returnTo) {
      this.state.orderData.return = returnTo;
    }

    OrderUtil.submit(this.state.orderData, PersonStore.getState().token)
      .then(data => {
        // SCA action support
        if (data.requires_action) {
          const stripe = Object.values(window.Stripe.__cachedInstances)[0];
          return stripe
            .handleCardAction(data.data.client_secret)
            .then(result => {
              if (result.error) {
                throw new Error(result.error.message);
              } else {
                return OrderUtil.submit(
                  Object.assign({}, this.state.orderData, {
                    payment_intent: result.paymentIntent.id
                  }),
                  PersonStore.getState().token
                );
              }
            });
        } else {
          return data;
        }
      })
      .then(data => {
        if (this.state.orderData.return) {
          window.location.href = this.state.orderData.return;
          return;
        }

        OrderActions.orderCompleted.defer(data);
      })
      .catch(OrderActions.orderFailed);
  }

  onOrderCompleted(data) {
    this.state.orders[data.id] = data;
    this.state.completedOrderId = data.id;
    this.setState(this.state);
    OrderActions.orderSucceeded.defer();
  }

  onUpdateCurrentOrder(data) {
    if (data.seats_requested && !this.state.orderData.seats_confirmed) {
      OrderUtil.holdSeats(Object.assign({}, this.state.orderData, data)).then(
        response => {
          this.setState({
            orderData: assign(this.state.orderData, {
              reservation_token: response.reservation_token,
              seats_confirmed: true,
              seats_requested: false
            }),
            orderSeatTimeout: response.expiry
          });
        }
      );
    } else if (data.seats_confirmed === false) {
      OrderActions.dropReservedSeats.defer();
    }

    this.setState({
      orderData: assign(this.state.orderData, data)
    });
  }

  onPushCurrentOrderToCart() {
    let subitems = this.state.cartData.subitems,
      newSubitems = this.state.orderData.subitems.filter(
        item => item.quantity > 0
      ), // only include non-zero quantity
      newCartData = assign({}, this.state.cartData, this.state.orderData, {
        subitems: subitems.concat(newSubitems)
      });

    this.setState({
      cartData: newCartData
    });

    let quantity = newSubitems.reduce((c, i) => c + i.quantity, 0);
    GlobalNotificationActions.notify.defer({
      message: `${quantity} ticket${quantity !== 1 ? "s" : ""} added to cart.`,
      action: {
        label: "Checkout",
        onClick: () => history.push(`/schedule/checkout`)
      }
    });
  }

  onUpdateCart(data) {
    let subitems = this.state.cartData.subitems,
      newSubitems = data.subitems,
      newCartData = assign({}, this.state.cartData, data, {
        subitems: newSubitems.filter(item => item.quantity > 0)
      });

    this.setState({
      cartData: newCartData
    });
  }

  onOrderSucceeded() {
    this.setState({
      orderSuccess: true,
      orderInProgress: false,
      orderErrorMessage: null
    });

    let order = this.state.orders[this.state.completedOrderId];
    if (order.passes.length) {
      PassActions.fetchAll.defer();
    }
    if (order.tickets.length) {
      EventActions.fetchOne.defer(order.tickets[0].event.id); // so we can populate fast
      EventActions.fetchAll.defer();
    }
    if (
      order.credits.length ||
      order.credit_record_event ||
      (AppStore.getState().settings &&
        AppStore.getState().settings.credits_enabled &&
        order.passes.length)
    ) {
      // passes can trigger credit refresh
      CreditActions.fetchCredits.defer();
    }

    if (
      window.__EVENTIVE_EVERYWHERE_UNIVERSAL_ON_ACTION &&
      order.tickets.length
    ) {
      window.__EVENTIVE_EVERYWHERE_UNIVERSAL_ON_ACTION({
        target: { event_id: order.tickets[0].event.id },
        action: "order_placed"
      });
    }
  }

  onOrderFailed(e) {
    this.setState({
      orderSuccess: false,
      orderInProgress: false,
      orderErrorMessage: e.message || e.code,
      orderShowDonateInterstitial: false
    });
  }

  onDownloadReceipt(id) {
    OrderUtil.downloadReceipt(PersonStore.getState().token, id);
  }

  onDropReservedSeats() {
    if (this.state.orderData.reservation_token) {
      OrderUtil.dropSeats(this.state.orderData).then(data => {
        // We must remove the reservation_token from the order data after dropping the seats
        let orderData = assign(this.state.orderData, {
          reservation_token: null
        });
        this.setState({
          seats_confirmed: false,
          seats_requested: false,
          orderSeatTimeout: null,
          orderData: orderData
        });
      });
    }
  }
}

let store = alt.createStore(OrderStore, "OrderStore");

if (SHOULD_PERSIST) {
  store.listen(state => {
    try {
      window.localStorage[LS_KEY] = JSON.stringify(state.cartData);
    } catch (e) {
      // Persistence failed :(
    }
  });
}

export default store;



// WEBPACK FOOTER //
// ./src/app/stores/OrderStore.js